// Patient PWA API client — the signed-in patient's own treatment + wear tracking, plus the staff
// endpoints for granting/resetting a case's patient login.
import { api } from './api.ts';
import { uploadWithProgress } from './uploadWithProgress.ts';
import type {
  PatientTreatment,
  PatientWear,
  PatientAccessResult,
  PatientAccessStatus,
  PhotoPose,
  ProgressPhoto,
} from '@alignova/shared';

export const patientApi = {
  me: () => api.get<PatientTreatment>('/patient/me'),
  wear: () => api.get<PatientWear>('/patient/wear'),
  startWear: () => api.post<PatientWear>('/patient/wear/start', {}),
  stopWear: () => api.post<PatientWear>('/patient/wear/stop', {}),
  photos: () => api.get<ProgressPhoto[]>('/patient/photos'),
  // staff
  accessStatus: (caseId: string) => api.get<PatientAccessStatus>(`/patient/access/${caseId}`),
  grantAccess: (caseId: string) => api.post<PatientAccessResult>(`/patient/access/${caseId}`, {}),
  resetAccess: (caseId: string) => api.post<PatientAccessResult>(`/patient/access/${caseId}/reset`, {}),
};

/** Send one progress photo. Multipart, so it goes through XHR rather than the JSON wrapper. */
export function uploadProgressPhoto(
  file: File,
  meta: { pose: PhotoPose; note?: string },
  onProgress?: (pct: number) => void,
): Promise<ProgressPhoto[]> {
  const fd = new FormData();
  fd.append('photo', file);
  fd.append('pose', meta.pose);
  if (meta.note) fd.append('note', meta.note);
  return uploadWithProgress<ProgressPhoto[]>('/api/patient/photos', fd, onProgress);
}

/** The patient's own photo, streamed inline. */
export const patientPhotoUrl = (photoId: string) => `/api/patient/photos/${photoId}/image`;
